// Castintech dashboard — Holding KPI Çekirdeği view modülü.
// Aşama G G7 (2026-05-29, BD-2026-05-28-002) — 20 KPI, 5 kategori.
// Meter weekly snapshot (Pazartesi 08:00) yazar; bu view okur.
//
// İçerik:
//   - 5 kategori grup (revenue / satisfaction / quality / ops / governance)
//   - Her KPI: değer + birim + trend (current vs previous snapshot)
//   - external_pending KPI'lar "Meter bekliyor" rozeti

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, $ } from '../utils.js';
import { createSkeleton } from '../ui/state-display.js';

interface Kpi {
  kpi_key: string;
  kpi_label: string;
  kpi_value: number | null;
  kpi_unit: string;
  kpi_category: string;
  source_kind: string;
  snapshot_date: string;
  prev_value: number | null;
}

let _kpis: Kpi[] = [];
let _latestDate: string | null = null;
let _error: string | null = null;
let _loaded = false;
// D8 — Değer & Emek (value-effort) gölge fiyat özeti (iç)
let _ve: any = null;

const CATEGORY_META: Record<string, { label: string; icon: string; color: string }> = {
  revenue: { label: 'Gelir', icon: '💰', color: '#22c55e' },
  satisfaction: { label: 'Memnuniyet', icon: '⭐', color: '#A8E6CF' },
  quality: { label: 'Kalite', icon: '🎯', color: '#60a5fa' },
  ops: { label: 'Operasyon', icon: '🚀', color: '#f59e0b' },
  governance: { label: 'Yönetişim', icon: '🏛️', color: '#818cf8' },
};

export async function loadHoldingKpi(): Promise<void> {
  if (!_loaded && state.currentPage === 'holding-kpi') {
    const root = $('holding-kpi-content');
    if (root && !root.querySelector('.kpi-layout')) {
      root.innerHTML = '';
      root.appendChild(createSkeleton({ rows: 5, height: '90px', rowGap: '12px' }));
    }
  }
  try {
    const r = await apiFetch('/castintech-api/holding-kpi');
    if (!r.ok) throw new Error('holding-kpi HTTP ' + r.status);
    const d = await r.json() as { kpis: Kpi[]; latest_snapshot: string | null };
    _kpis = d.kpis || [];
    _latestDate = d.latest_snapshot;
    _error = null;
  } catch (e) {
    _error = (e as Error).message || 'KPI verisi yüklenemedi';
    console.warn('loadHoldingKpi failed:', _error);
  } finally {
    _loaded = true;
  }
  // D8 — Değer & Emek özeti (best-effort, iç)
  try {
    const rv = await apiFetch('/castintech-api/value-effort/summary');
    if (rv.ok) _ve = await rv.json();
  } catch { /* yok say */ }
  if (state.currentPage === 'holding-kpi') renderHoldingKpi();
}

function veCard(): string {
  if (!_ve || !_ve.ok) return '';
  const cur = _ve.currency || 'TRY';
  const money = (n: number) => Number(n || 0).toLocaleString('tr-TR') + ' ' + cur;
  const b = _ve.billable || { value: 0, hours: 0, count: 0 };
  const f = _ve.free || { value: 0, hours: 0, count: 0 };
  const freeT = (_ve.per_tenant || []).filter((t: any) => t.free && (t.free.value || t.free.hours))
    .sort((a: any, x: any) => x.free.value - a.free.value).slice(0, 8);
  const rows = freeT.map((t: any) => `<tr><td>${escHtml(t.display_name)}</td><td>${escHtml(t.relationship || '')}</td><td>${money(t.free.value)}</td><td>${Math.round(t.free.hours)} sa</td></tr>`).join('');
  return `<div class="ve-card">
    <div class="ve-head">💎 Değer & Emek <span class="ve-sub">gölge fiyat · iç ölçüm (fatura değil)</span></div>
    <div class="ve-tiles">
      <div class="ve-tile"><div class="ve-t-lbl">Faturalanan iş</div><div class="ve-t-val" style="color:var(--gem-emerald,#34d399)">${money(b.value)}</div><div class="ve-t-sub">${Math.round(b.hours)} saat · ${b.count} iş</div></div>
      <div class="ve-tile"><div class="ve-t-lbl">Ücretsiz / Partner iş</div><div class="ve-t-val" style="color:var(--gem-amber,#fbbf24)">${money(f.value)}</div><div class="ve-t-sub">${Math.round(f.hours)} saat · ${f.count} iş</div></div>
    </div>
    ${rows ? `<table class="gb-table ve-table"><thead><tr><th>Tenant</th><th>İlişki</th><th>Gölge değer</th><th>Emek</th></tr></thead><tbody>${rows}</tbody></table>` : ''}
    <div class="ve-note">Placeholder rate-card (service-rate-card.json) — Sapphire/Yılmaz düzeltir. Ücretsiz/partner = harcanan değer+emek (opportunity-cost). Sadece iç.</div>
  </div>`;
}

function trendArrow(cur: number | null, prev: number | null): string {
  if (cur === null || prev === null) return '';
  if (cur > prev) return `<span class="kpi-trend kpi-trend--up" title="önceki: ${prev}">▲</span>`;
  if (cur < prev) return `<span class="kpi-trend kpi-trend--down" title="önceki: ${prev}">▼</span>`;
  return `<span class="kpi-trend kpi-trend--flat" title="değişmedi">→</span>`;
}

function fmtValue(k: Kpi): string {
  if (k.kpi_value === null) return '<span class="kpi-pending">— bekliyor</span>';
  let v = String(k.kpi_value);
  if (k.kpi_unit === '₺') v = '₺' + Number(k.kpi_value).toLocaleString('tr-TR');
  else if (k.kpi_unit === '%') v = k.kpi_value + '%';
  else if (k.kpi_unit && k.kpi_unit !== 'adet') v = k.kpi_value + ' ' + k.kpi_unit;
  return v;
}

export function renderHoldingKpi(): void {
  const root = $('holding-kpi-content');
  if (!root) return;
  const sub = $('holding-kpi-sub');
  if (sub) sub.textContent = _latestDate
    ? `Son snapshot: ${_latestDate} · Meter weekly (Pzt 08:00) · ${_kpis.length} KPI`
    : '20 KPI · 5 kategori · Meter weekly';

  if (_error) {
    root.innerHTML = `<div class="ds-error" role="alert" style="margin:8px 0">
      <div class="ds-error-icon" aria-hidden="true">⚠️</div>
      <div class="ds-error-title">KPI verisi yüklenemedi</div>
      <div class="ds-error-desc">${escHtml(_error)}</div>
      <button class="ds-error-retry" type="button" id="kpi-retry-btn">Tekrar dene</button>
    </div>`;
    const btn = root.querySelector<HTMLButtonElement>('#kpi-retry-btn');
    if (btn) btn.addEventListener('click', () => { void loadHoldingKpi(); });
    return;
  }

  if (_kpis.length === 0) {
    root.innerHTML = veCard() + `<div class="ds-empty" role="status" style="margin:8px 0">
      <div class="ds-empty-icon" aria-hidden="true">📊</div>
      <div class="ds-empty-title">Henüz KPI snapshot yok</div>
      <div class="ds-empty-desc">Meter Pazartesi 08:00 cron'unda ilk snapshot'ı yazar. Manuel tetik için POST /holding-kpi/snapshot.</div>
    </div>`;
    return;
  }

  // Kategoriye göre grupla
  const order = ['revenue', 'satisfaction', 'quality', 'ops', 'governance'];
  const groups: Record<string, Kpi[]> = {};
  for (const k of _kpis) (groups[k.kpi_category] ||= []).push(k);

  const groupsHtml = order.filter(c => groups[c]).map(cat => {
    const meta = CATEGORY_META[cat] || { label: cat, icon: '•', color: 'var(--muted)' };
    const cards = groups[cat].map(k => `
      <div class="kpi-card${k.source_kind === 'external_pending' ? ' kpi-card--pending' : ''}">
        <div class="kpi-card-label">${escHtml(k.kpi_label)}</div>
        <div class="kpi-card-value">${fmtValue(k)} ${trendArrow(k.kpi_value, k.prev_value)}</div>
        ${k.source_kind === 'external_pending' ? '<div class="kpi-card-badge">Meter bekliyor</div>' : ''}
      </div>`).join('');
    return `
      <div class="kpi-group">
        <div class="kpi-group-header" style="border-left:3px solid ${meta.color}">
          <span class="kpi-group-icon">${meta.icon}</span>
          <span class="kpi-group-title">${escHtml(meta.label)}</span>
          <span class="kpi-group-count">${groups[cat].length}</span>
        </div>
        <div class="kpi-group-grid">${cards}</div>
      </div>`;
  }).join('');

  const pendingCount = _kpis.filter(k => k.source_kind === 'external_pending').length;
  root.innerHTML = `
    <div class="kpi-layout">
      ${veCard()}
      ${groupsHtml}
      <div style="margin-top:12px;font-size:11px;color:var(--muted)">
        ${pendingCount > 0 ? `⏳ ${pendingCount} metrik Meter tarafından doldurulacak (external veri). ` : ''}
        Trend: ▲ artış · ▼ azalış · → sabit (önceki snapshot ile) · CHARTER § 1.5 K5
      </div>
    </div>`;
}
